import { useState } from "react";
import { VideoOff } from "lucide-react";
import { saveFile } from "@/lib/saveFile";

type Props = {
  /** Local (already fetched + decrypted) object URL of the video. */
  url: string;
  filename: string;
};

/**
 * Shown when a small video's bytes arrived intact but the <video> element
 * failed to decode them — in practice a codec this browser can't play (the
 * classic case: iPhone-camera HEVC/QuickTime opened in a browser without an
 * HEVC decoder; Safari/Apple hardware plays it, codec-less engines don't).
 *
 * A Retry is deliberately NOT offered: the bytes are already local, so a
 * refetch redecodes the same undecodable data. The honest exit is Download —
 * the file itself is fine and plays in a native player.
 */
export default function UnplayableVideoCard({ url, filename }: Props) {
  const [saving, setSaving] = useState(false);

  async function handleDownload() {
    if (!url || saving) {
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        return;
      }
      await saveFile(await response.blob(), filename);
    } catch {
      // Leave the button enabled for another attempt.
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="alert"
      className="flex h-full flex-col items-center justify-center gap-3 text-sm text-muted-foreground"
    >
      <VideoOff className="size-8" />
      <p>This video can&apos;t be played in this browser.</p>
      <p className="max-w-xs text-center text-xs opacity-80">
        Its format isn&apos;t supported here. Download it to watch it in another
        app.
      </p>
      <button
        type="button"
        onClick={handleDownload}
        disabled={!url || saving}
        className="rounded-md bg-primary/20 px-4 py-1.5 text-foreground hover:bg-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        {saving ? "Preparing…" : "Download"}
      </button>
    </div>
  );
}
